import { ClaimScanProcess } from './ClaimScanProcess'
import { ExpiredBlock } from './ExpiredBlock'
import { OwnerBlock } from './OwnerBlock'
import { TransferOwnershipInfo } from './TransferOwnershipInfo'

import React, { useState, useEffect } from 'react'

import { Search } from 'components/Fields/Search'
import { FieldText } from 'components/Fields/Text'
import { Icon } from 'components/UI'
import { CountdownTimer } from 'components/UI/CountdownTimer'

import { getCustomers } from 'store/customers/actions'
import { useDispatch, useSelector } from 'store/hooks'
import { getName } from 'utils/parsers'

import { useTransferCertificate } from 'hooks/api/useTransferCertificate'
import { useForm } from 'react-hook-form'
import type { CertificateCustomer, Customer } from 'types'

import { Box, Divider, Stack, Typography, Button } from '@mui/material'
import { useGetCertificatePublic } from 'hooks/api/useGetCertificatePublic'
import { useQueryClient } from '@tanstack/react-query'

interface TransferOwnershipProps {
  certificate: CertificateCustomer
  closeTransfer: () => void
  onExpired?: () => void
}

export const TransferOwnership = ({ certificate, closeTransfer, onExpired }: TransferOwnershipProps) => {
  const methods = useForm()
  const dispatch = useDispatch()
  const queryClient = useQueryClient();
  const { handleSubmit, resetField } = methods

  const { customers } = useSelector(({ customers }) => ({
    customers: customers.customers,
  }));

  const { data: certificateData } = useGetCertificatePublic(certificate.id);

  const { isStatusChanged, isTransferConfirmed, transfer, confirmTransfer, cancelTransfer } = useTransferCertificate()

  const [selectedUser, setSelectedUser] = useState<Customer | null>(null)
  const [searchValue, setSearchValue] = useState<string>('')
  const [isExpired, setIsExpired] = useState<boolean>(false)
  const [salePriceValue, setSalePriceValue] = useState<number>()

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['certificate-public', certificate.id] });
  }, [isStatusChanged, isExpired])

  const transferCertificateHandle = handleSubmit(data => {
    transfer({
      certificateId: certificate.id,
      customerEmail: selectedUser?.email,
      price: Number(data?.salePrice),
    })
  })

  const handleSearchInputChange = (value: string) => {
    setSearchValue(value)
    dispatch(getCustomers({ query: value, limit: 10 }))
  }

  const handleSalePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setSalePriceValue(Number(value))
    methods.setValue('salePrice', value)
    methods.trigger('salePrice')
  }

  const clearSearch = () => {
    setSelectedUser(null)
    setSearchValue('')
    resetField('search')
  }

  const confirmTransferHandle = () => {
    confirmTransfer({ certificateId: certificate.id })
  }

  const cancelTransferHandle = () => {
    cancelTransfer({ certificateId: certificate.id })
  }

  const expireHandler = () => {
    setIsExpired(true)
    cancelTransferHandle()
    onExpired && onExpired()
  }

  const certificateStatus = certificateData?.status || certificate?.status
  const transferWaitingConfirmationStatus = certificateStatus === 'transfer_waiting_confirmation'
  const activeStatus = certificateStatus === 'active'
  const transferInitiatedStatus = certificateStatus === 'transfer_initiated'

  return (
    <Stack direction="column">
      {transferInitiatedStatus && (
        <ClaimScanProcess
          certificate={certificate}
          closeTransfer={closeTransfer}
          onExpiredHandler={expireHandler}
          title="To finish the process of transfering, hold your phone near your item to scan"
        />
      )}

      {(transferWaitingConfirmationStatus || activeStatus) &&
        !isTransferConfirmed &&
        (isExpired ? (
          <ExpiredBlock closeTransfer={closeTransfer} />
        ) : (
          <>
            <TransferOwnershipInfo
              certificate={certificate}
              selectedUser={selectedUser}
              transferWaitingConfirmationStatus={transferWaitingConfirmationStatus}
            />
            {selectedUser || transferWaitingConfirmationStatus ? (
              <>
                <Stack
                  mb="20px"
                  sx={{
                    '.MuiFormControl-root': {
                      flexDirection: 'row',
                      alignItems: 'center',
                      '.MuiFormHelperText-root': {
                        margin: '16px 16px 0',
                      },
                    },
                  }}
                >
                  <FieldText
                    {...methods}
                    name="salePrice"
                    defaultValue={transferWaitingConfirmationStatus ? certificate.transfer.price : ''}
                    label={transferWaitingConfirmationStatus ? 'Price for product' : 'Sale price'}
                    disabled={transferWaitingConfirmationStatus}
                    sx={{
                      maxWidth: '140px',
                    }}
                    startAdornment={(salePriceValue || transferWaitingConfirmationStatus) && '$'}
                    type="number"
                    rules={{
                      maxLength: {
                        value: 10,
                        message: 'The maximum number of characters allowed is 10',
                      },
                      required: true,
                    }}
                    onChange={e => handleSalePriceChange(e)}
                  />
                </Stack>

                {!transferWaitingConfirmationStatus && (
                  <Stack flexDirection="row" alignItems="center" mb={2}>
                    <Icon name="info" sx={{ marginRight: '12px' }} />
                    <Typography variant="body2" sx={{ lineHeight: '20px' }}>
                      The price will only be visible to you and the buyer.
                    </Typography>
                  </Stack>
                )}

                <Divider />

                {transferWaitingConfirmationStatus ? (
                  <>
                    <Stack flexDirection="row" sx={{ margin: '16px 0' }}>
                      <Icon name="info" sx={{ marginRight: '12px' }} />
                      <Typography variant="body2" sx={{ lineHeight: '20px' }}>
                        Notice: <br />
                        You are about to transfer this item to a new owner. You are only able to cancel a transfer from
                        your account prior to receiving confirmation from the buyer.
                      </Typography>
                    </Stack>

                    <Stack gap="12px" sx={{ width: '100%', marginTop: '24px' }}>
                      <Button
                        fullWidth
                        onClick={confirmTransferHandle}
                      >
                        Transfer Ownership
                      </Button>

                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={cancelTransferHandle}
                      >
                        Cancel Transfer in
                        {certificate.transfer.expire_at && (
                          <CountdownTimer expiredAt={certificate.transfer.expire_at} timeIsOut={expireHandler} />
                        )}
                      </Button>
                    </Stack>
                  </>
                ) : (
                  <Stack gap="12px" sx={{ width: '100%', marginTop: '24px' }}>
                    <Button
                      fullWidth
                      onClick={transferCertificateHandle}
                    >
                      Scan Item to Confirm Transfer
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={clearSearch}
                    >
                      Cancel
                    </Button>
                  </Stack>
                )}
              </>
            ) : (
              <Stack sx={{ height: '309px' }}>
                <Search
                  onChange={handleSearchInputChange}
                  placeholder="Type email to find or invite new user"
                  sx={{ borderRadius: '999px' }}
                />
                {customers?.data && searchValue && (
                  <Box
                    mt="4px"
                    p={2}
                    sx={theme => ({
                      boxShadow: '0px 5px 8px -2px rgba(74, 85, 107, 0.18), 0px 0px 3px 0px rgba(74, 85, 107, 0.16)',
                      overflow: 'auto',
                    })}
                  >
                    {customers?.data.map((customer, index) => (
                      <OwnerBlock
                        key={customer.id}
                        owner={getName(customer) || customer.email}
                        imageUrl={customer.avatar}
                        subTitle={customer.email}
                        isLast={index !== customers?.data.length - 1}
                        onClick={() => setSelectedUser(customer)}
                      />
                    ))}
                  </Box>
                )}
                {!customers?.data && searchValue && (
                  <Stack gap="12px" sx={{ width: '100%', marginTop: '24px' }}>
                    <Typography variant="body2" sx={{ lineHeight: '20px' }}>
                      No customers found
                    </Typography>
                    <Button variant="outlined" fullWidth onClick={clearSearch}>
                      Clear search
                    </Button>
                    <Button variant="outlined" fullWidth onClick={clearSearch}>
                      Invite new user
                    </Button>
                  </Stack>
                )}
              </Stack>
            )}
          </>
        ))}

      {isTransferConfirmed && (
        <Box sx={{ textAlign: 'center' }}>
          <Typography mb="16px" variant="body1">
            Success
          </Typography>
          <Typography m="0 8px 16px" variant="body1">
            A notification to confirm is sent to the buyer. You are only able to cancel a transfer from your account
            prior to receiving confirmation.
          </Typography>
          <Typography variant="h2">{certificate.product.title}</Typography>
          <Typography my="4px" variant="body2">
            {certificate.product.sku}
          </Typography>
          <Box mb="24px">
            <Icon name="success" sx={{ width: '56px', height: '56px' }} />
          </Box>
          <Button
            fullWidth
            onClick={closeTransfer}
          >
            Done
          </Button>
        </Box>
      )}
    </Stack>
  )
}
